
import React from "react";
import { CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CountyIntroductionProps {
  countyName: string;
  nursingHomeCount: number;
  description?: string;
}

export function CountyIntroduction({ countyName, nursingHomeCount, description }: CountyIntroductionProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
      <div className="max-w-4xl">
        <h2 className="text-2xl font-semibold mb-4 text-grandpal-green-dark">
          About Nursing Homes in {countyName}
        </h2>
        
        {description ? (
          <div className="text-grandpal-gray mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: description }} />
        ) : (
          <p className="text-grandpal-gray mb-4 leading-relaxed">
            {countyName} is home to over {nursingHomeCount} nursing homes offering a wide range of elder care services—from luxury private care to public HSE-supported homes. 
            At Grandpal, we provide an independent, trusted platform to help you find the best care options in {countyName}.
          </p>
        )}
        
        <div className="mb-6">
          <h3 className="font-medium text-lg mb-3">Our platform helps you:</h3>
          <ul className="space-y-2">
            {[
              `Understand your options in ${countyName}`,
              "Check availability and join waitlists",
              "Access Fair Deal eligibility information",
              "Connect directly with nursing homes"
            ].map((benefit, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-grandpal-green mr-2 mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="flex flex-col md:flex-row md:items-center bg-grandpal-beige/30 p-4 rounded-lg">
          <Phone className="h-5 w-5 text-grandpal-green mr-3 mb-2 md:mb-0" />
          <p className="text-sm font-medium mr-4 mb-2 md:mb-0">Need help deciding? Our advisors can assist you in narrowing down your options in {countyName}.</p>
          <Button className="bg-grandpal-green hover:bg-grandpal-green-dark whitespace-nowrap md:ml-auto">
            Talk to an advisor
          </Button>
        </div>
      </div>
    </div>
  );
}
